import { useEffect, useRef, useState } from "react";
import {
    CalendarOutlined,
    CompassOutlined,
    CreditCardOutlined,
    EnvironmentOutlined,
} from "@ant-design/icons";

// Marketing illustration of the app in use, shown inside the split hero's
// phone frame. Purely decorative — plain divs, no real data, no
// interactivity — but it auto-cycles through a few hand-drawn "screens" to
// read as someone tapping through Overview → Itinerary → Map → Expenses.
// Colours come from the shared --gt-* tokens.

const SCREENS = ["Overview", "Itinerary", "Map", "Expenses"] as const;
const TAB_ICONS = [
    <EnvironmentOutlined key="overview" />,
    <CalendarOutlined key="itinerary" />,
    <CompassOutlined key="map" />,
    <CreditCardOutlined key="expenses" />,
];
const DAY_COLORS = [
    "var(--gt-primary)",
    "var(--gt-accent)",
    "var(--gt-feature-expense)",
];
const CYCLE_MS = 3200;

function OverviewScreen() {
    return (
        <div className="gt-hp__body">
            <div className="gt-hp__countdown">
                <div className="gt-eyebrow gt-hp__eyebrow">Departure</div>
                <div className="gt-hp__count">
                    <span className="gt-hp__count-num">12</span>
                    <span className="gt-hp__count-unit">days to go</span>
                </div>
                <div className="gt-hp__weather">⛅ 14°/8° · 20% rain</div>
            </div>
            <div className="gt-hp__spend">
                <div className="gt-eyebrow gt-hp__spend-label">Spending</div>
                <div className="gt-hp__bars">
                    <div className="gt-hp__bar">
                        <span className="gt-hp__bar-label">Fuel</span>
                        <div className="gt-hp__track">
                            <div
                                className="gt-hp__fill"
                                style={{ width: "82%", background: "var(--gt-primary)" }}
                            />
                        </div>
                    </div>
                    <div className="gt-hp__bar">
                        <span className="gt-hp__bar-label">Hotel</span>
                        <div className="gt-hp__track">
                            <div
                                className="gt-hp__fill"
                                style={{ width: "58%", background: "var(--gt-feature-expense)" }}
                            />
                        </div>
                    </div>
                    <div className="gt-hp__bar">
                        <span className="gt-hp__bar-label">Food</span>
                        <div className="gt-hp__track">
                            <div
                                className="gt-hp__fill"
                                style={{ width: "40%", background: "var(--gt-accent)" }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="gt-hp__people">
                <div className="gt-hp__person">
                    <span className="gt-hp__person-id">
                        <span className="gt-hp__avatar" style={{ background: "var(--gt-primary)" }}>
                            JM
                        </span>
                        <span className="gt-hp__name">James M.</span>
                    </span>
                    <span className="gt-hp__chip gt-hp__chip--owed">Owed £48</span>
                </div>
                <div className="gt-hp__person">
                    <span className="gt-hp__person-id">
                        <span className="gt-hp__avatar" style={{ background: "var(--gt-feature-team)" }}>
                            SK
                        </span>
                        <span className="gt-hp__name">Sam K.</span>
                    </span>
                    <span className="gt-hp__chip gt-hp__chip--owes">Owes £26</span>
                </div>
                <div className="gt-hp__person">
                    <span className="gt-hp__person-id">
                        <span className="gt-hp__avatar" style={{ background: "var(--gt-feature-expense)" }}>
                            PR
                        </span>
                        <span className="gt-hp__name">Priya R.</span>
                    </span>
                    <span className="gt-hp__chip gt-hp__chip--owes">Owes £22</span>
                </div>
            </div>
        </div>
    );
}

function ItineraryScreen() {
    const days = [
        { title: "Bilbao → Pamplona", meta: "142 km · 2h 40m" },
        { title: "Pamplona → Pyrenees pass", meta: "98 km · 2h 05m" },
        { title: "Pyrenees → Barcelona", meta: "216 km · 3h 15m" },
    ];
    return (
        <div className="gt-hp__body">
            <div className="gt-hp__title">Itinerary</div>
            <div className="gt-hp__days">
                {days.map((d, i) => (
                    <div className="gt-hp__day" key={d.title}>
                        <span className="gt-hp__day-badge" style={{ background: DAY_COLORS[i] }}>
                            {i + 1}
                        </span>
                        <div className="gt-hp__day-info">
                            <div className="gt-hp__day-title">{d.title}</div>
                            <div className="gt-hp__day-meta">{d.meta}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function MapScreen() {
    return (
        <div className="gt-hp__body">
            <div className="gt-hp__title">Route</div>
            <div className="gt-hp__map">
                <svg viewBox="0 0 240 130" width="100%">
                    <path
                        d="M16 100 Q 70 40 120 66 T 224 30"
                        stroke="var(--gt-primary)"
                        strokeWidth="2.5"
                        strokeDasharray="1 9"
                        strokeLinecap="round"
                        fill="none"
                    />
                    <circle cx="16" cy="100" r="5" fill="var(--gt-primary)" />
                    <circle cx="120" cy="66" r="5" fill="var(--gt-accent)" />
                    <circle cx="224" cy="30" r="5" fill="var(--gt-feature-expense)" />
                    <text x="16" y="118" fontSize="9" fill="var(--gt-text-muted)">
                        Bilbao
                    </text>
                    <text x="224" y="20" fontSize="9" fill="var(--gt-text-muted)" textAnchor="end">
                        Barcelona
                    </text>
                </svg>
            </div>
            <div className="gt-hp__map-legend">
                {["Day 1", "Day 2", "Day 3"].map((label, i) => (
                    <span className="gt-hp__map-legend-item" key={label}>
                        <i className="gt-hp__map-dot" style={{ background: DAY_COLORS[i] }} />
                        {label}
                    </span>
                ))}
            </div>
        </div>
    );
}

function ExpensesScreen() {
    const rows = [
        { label: "Fuel", amount: "£42", color: "var(--gt-primary)" },
        { label: "Hotel", amount: "£132", color: "var(--gt-feature-expense)" },
        { label: "Dinner", amount: "£28", color: "var(--gt-accent)" },
    ];
    return (
        <div className="gt-hp__body">
            <div className="gt-hp__title">Expenses</div>
            <div className="gt-hp__expenses">
                {rows.map((r) => (
                    <div className="gt-hp__expense-row" key={r.label}>
                        <span className="gt-hp__expense-id">
                            <i className="gt-hp__expense-dot" style={{ background: r.color }} />
                            <span className="gt-hp__expense-label">{r.label}</span>
                        </span>
                        <span className="gt-hp__expense-amount">{r.amount}</span>
                    </div>
                ))}
                <div className="gt-hp__expense-row gt-hp__expense-row--total">
                    <span>Total</span>
                    <span>£202</span>
                </div>
            </div>
        </div>
    );
}

const SCREEN_COMPONENTS = [OverviewScreen, ItineraryScreen, MapScreen, ExpensesScreen];

export default function HeroAppPreview() {
    const [active, setActive] = useState(0);
    const reducedMotion = useRef(false);

    useEffect(() => {
        reducedMotion.current = window.matchMedia(
            "(prefers-reduced-motion: reduce)",
        ).matches;
        if (reducedMotion.current) return;
        const id = window.setInterval(() => {
            setActive((i) => (i + 1) % SCREEN_COMPONENTS.length);
        }, CYCLE_MS);
        return () => window.clearInterval(id);
    }, []);

    const Screen = SCREEN_COMPONENTS[active];

    return (
        <div className="gt-phone gt-hero-preview" aria-hidden="true">
            <div className="gt-phone__screen">
                <div className="gt-phone__notch" />
                <div className="gt-hp__header">The Stelvio</div>
                <div className="gt-hp__frame" key={active}>
                    <Screen />
                </div>
                <div className="gt-hp__tabs">
                    {SCREENS.map((label, i) => (
                        <div
                            className={`gt-hp__tab${i === active ? " gt-hp__tab--active" : ""}`}
                            key={label}
                        >
                            {TAB_ICONS[i]}
                            <span>{label}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
